import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useStylingNoteLLM } from "@/lib/useStylingNoteLLM";
import { cleanProductTitle } from "@/lib/titleClean";
import { LensSystem } from "@/components/confidence/LensSystem";

import { toast } from "sonner";
import { requestAccountFocus, type AccountFocusSection } from "@/lib/accountFocus";


import { supabase } from "@/integrations/supabase/client";
import type { WardrobeItem } from "@/components/Wardrobe";
import GuestProgressiveFit from "@/components/GuestProgressiveFit";
import SizePills from "@/components/results/confidence/SizePills";
import { LookbookLoadingSilhouette } from "@/components/LookbookLoadingSilhouette";
import {
  readGuestModelProfile,
  type GuestModelProfile,
} from "@/lib/profileFieldOptions";

// PR 4d — Guest-path first-render hydration. Reads sessionStorage on mount so
// assessmentCtx has user_size + user_gender BEFORE GuestProgressiveFit mounts
// and emits onProfileChange. Without this, the first generateConfidenceAssessment
// call runs with null gender → brand_fit_profiles lookup skipped → unified
// scoring falls through to the hedged template "Likely size L — your usual fit".
function readGuestSizeFromStorage(): {
  size: string | null;
  sizeNumber: string | null;
  gender: Gender | null;
} {
  if (typeof window === "undefined") return { size: null, sizeNumber: null, gender: null };
  try {
    const raw = window.sessionStorage.getItem("l2f_guest_size");
    if (!raw) return { size: null, sizeNumber: null, gender: null };
    const parsed = JSON.parse(raw) as {
      top_letter?: string | null;
      top_uk_number?: string | null;
      gender?: string | null;
    };
    const g = parsed.gender;
    return {
      size: parsed.top_letter ?? null,
      sizeNumber: parsed.top_uk_number ?? null,
      gender: g === "men" || g === "women" || g === "non-binary" ? (g as Gender) : null,
    };
  } catch {
    return { size: null, sizeNumber: null, gender: null };
  }
}
import {
  generateConfidenceAssessment,
  normaliseGender,
  type ConfidenceAssessment,
  type ConfidenceContext,
  type Gender,
  type MissingField,
  type UserBrandFitEntry,
} from "@/lib/guestReasoningTemplates";
import { retailerIdFromLink } from "@/lib/retailerSizeConvention";
import { trackEvent, setSignupTrigger } from "@/lib/analytics";
import {
  verdictFromAssessment,
  computePairsWithLens,
  computeFillsGapLens,
  type Verdict,
  type TierTag,
  type LensSystem as LensSystemType,
  type TaxonomyGender,
} from "@/lib/unifiedScoring";
import { normaliseBrand } from "@/lib/brandRegistry";
import { isInDefaultBrands, DISMISS_ALL_SENTINEL } from "@/lib/brandFitDefaults";
// Wave 3B — Commit 3j: PCC no longer hosts an offline-shopping badge.
// The decision surface is the shopping list (LB1/LB2/LB3), not the
// verdict card. Badge/import/props removed intentionally.

// PQ-5a — verdict copy (locked decisions). PQ-5e removed the 3-dot
// confidence meter: verdict word IS the confidence signal, lens system
// carries nuance.
const VERDICT_COPY: Record<Verdict, string> = {
  buy: "Buy it.",
  your_call: "Your call.",
  skip: "Skip it.",
};


/* ---------- Types (kept loose to match index.tsx shapes) ---------- */

type ShoppingProduct = {
  title: string;
  price: string;
  source: string;
  link: string;
  thumbnail: string;
};

export type TierName = "budget" | "mid" | "premium";

type Tiers = Partial<Record<TierName, ShoppingProduct | null>> & {
  your_pick?: ShoppingProduct | null;
  your_pick_is_caveat?: boolean;
} & Record<string, unknown>;

export type ConfidenceHeroItem = {
  id?: string;
  name: string;
  category: string;
  color_hex: string | null;
  thumbnail?: string | null;
  tiers: Tiers | null;
  // A-7a PR 5c — structured garment attributes from detected_items, plumbed
  // through assessmentCtx → ResolvedSignals.garment_attributes.
  silhouette?: string | null;
  length?: string | null;
  neckline?: string | null;
  sleeve_length?: string | null;
  fit?: string | null;
  fabric_weight?: string | null;
  structure?: string | null;
};

export type ConfidenceDetectedItem = {
  id: string;
  name: string;
  category: string;
  color_hex: string | null;
  item_type?: string | null;
  tiers: Tiers | null;
};

export type BrandFitEntry = {
  brand: string;
  verdict: string;
  category: string;
};

export type SizeProfileLite = Record<string, unknown> | null;

/* ---------- Design tokens ---------- */
const CORAL = "var(--l2f-coral)";
const CREAM = "var(--l2f-cream)";
const GOOD_BG = "var(--l2f-success-bg)";
const GOOD_BORDER = "var(--l2f-success-border)";
const GOOD_INK = "var(--l2f-success-ink-soft)";
const CHECK_BG = "var(--l2f-warning-bg)";
const CHECK_BORDER = "var(--l2f-warning-border)";

const CHECK_INK = "var(--l2f-warning-ink-soft)";
const MUTED_BG = "#F4F1EE";
const MUTED_BORDER = "rgba(0,0,0,0.08)";

/* ---------- Helpers ---------- */

const CONFIDENCE_DEBUG_KEY = "l2f_confidence_debug";

function shouldLogConfidenceDebug(): boolean {
  try {
    return (
      typeof window !== "undefined" &&
      (window.localStorage.getItem(CONFIDENCE_DEBUG_KEY) === "1" ||
        new URLSearchParams(window.location.search).get(CONFIDENCE_DEBUG_KEY) === "1")
    );
  } catch {
    return false;
  }
}

function confidenceDebug(label: string, payload: Record<string, unknown>): void {
  if (shouldLogConfidenceDebug()) console.log(`[confidence-debug] ${label}`, payload);
}

export type FitConfidence = {
  level: "good" | "check" | "low";
  headline: string;
  reasons: string[];
  warnings: string[];
};

export function getFitConfidence(
  heroItem: ConfidenceHeroItem,
  brandFitTops: BrandFitEntry[],
  brandFitBottoms: BrandFitEntry[],
  _sizeProfile: SizeProfileLite,
): FitConfidence {
  const cat = (heroItem.category ?? "").toLowerCase();
  const isTop = ["top", "outerwear"].includes(cat);
  const relevant = isTop ? brandFitTops : brandFitBottoms;
  const sourceRaw = heroItem.tiers?.mid?.source ?? "";
  const normalisedSource = normaliseBrand(sourceRaw);

  const brandMatch = normalisedSource
    ? relevant.find((b) => {
        const normalisedStored = normaliseBrand(b.brand);
        return normalisedStored && normalisedStored === normalisedSource;
      })
    : undefined;

  if (brandMatch) {
    if (brandMatch.verdict === "true_to_size") {
      return {
        level: "good",
        headline: "Looks like a good fit",
        reasons: [`✓ ${brandMatch.brand} fits you true to size`],
        warnings: [],
      };
    }
    if (brandMatch.verdict === "runs_small") {
      return {
        level: "check",
        headline: "Check your size",
        reasons: [],
        warnings: [`⚠ ${brandMatch.brand} runs small — consider sizing up`],
      };
    }
    if (brandMatch.verdict === "runs_large") {
      return {
        level: "check",
        headline: "Check your size",
        reasons: [],
        warnings: [`⚠ ${brandMatch.brand} runs large — consider sizing down`],
      };
    }
  }
  return {
    level: "low",
    headline: "Limited fit data",
    reasons: [],
    warnings: [
      "⚠ We don't have fit data for this brand yet",
      "⚠ Check the size guide before buying",
    ],
  };
}

const CATEGORY_PAIRS: Record<string, string[]> = {
  outerwear: ["top", "bottom", "dress", "shoes"],
  top: ["bottom", "shoes", "outerwear", "accessory"],
  bottom: ["top", "shoes", "outerwear", "accessory"],
  dress: ["shoes", "outerwear", "accessory", "bag"],
  shoes: ["top", "bottom", "dress", "outerwear"],
};

export function getVersatilityCount(
  heroItem: ConfidenceHeroItem,
  wardrobeItems: WardrobeItem[],
): { count: number; matchingItems: WardrobeItem[] } {
  const heroCategory = (heroItem.category ?? "").toLowerCase();
  const compat = CATEGORY_PAIRS[heroCategory] ?? [];
  const matchingItems = wardrobeItems.filter((i) =>
    i.category ? compat.includes(i.category.toLowerCase()) : false,
  );
  return { count: matchingItems.length, matchingItems };
}

/** Priority: outerwear → top → bottom → dress. */
export function selectHeroPieceFromItems<
  T extends { category: string; item_type?: string | null },
>(items: T[]): T | null {
  if (!items || items.length === 0) return null;
  const buyable = items.filter((i) => i.item_type !== "owned");
  const pool = buyable.length > 0 ? buyable : items;
  const priority = ["outerwear", "top", "bottom", "dress"];
  for (const cat of priority) {
    const found = pool.find((i) => (i.category ?? "").toLowerCase() === cat);
    if (found) return found;
  }
  return pool[0] ?? null;
}

/* ---------- UI subcomponents ---------- */

// PQ-5f — Swatch component removed (was used by versatility chips,
// permanently retired in F30.1). VersatilityCard owns the only live Swatch.

// BEST-VISUAL-MATCH-1 — inline "Your pick" row.
// Anchors the user on the Lens-#1 visual match (or best-scored fallback flagged
// as caveat) WITHOUT requiring them to expand the alternatives accordion.
// Renders on every verdict, including Skip.
function YourPickRow({
  outfitId,
  verdict,
  yourPick,
  isCaveat,
}: {
  outfitId: string | null;
  verdict: Verdict | null;
  yourPick: ShoppingProduct | null;
  isCaveat: boolean;
}) {
  const firedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!yourPick?.link) return;
    const key = `${outfitId ?? "_"}::${yourPick.link}`;
    if (firedRef.current === key) return;
    firedRef.current = key;
    try {
      trackEvent("your_pick_displayed", {
        outfit_id: outfitId,
        item_url: yourPick.link,
        is_caveat: isCaveat,
        verdict: verdict ?? null,
      });
    } catch {
      /* analytics best-effort */
    }
  }, [outfitId, yourPick?.link, isCaveat, verdict]);

  if (!yourPick || !yourPick.link) return null;

  const handleTap = () => {
    try {
      trackEvent("your_pick_tapped", {
        outfit_id: outfitId,
        item_url: yourPick.link,
        is_caveat: isCaveat,
        destination_url: yourPick.link,
      });
    } catch {
      /* analytics best-effort */
    }
    try {
      window.open(yourPick.link, "_blank", "noopener,noreferrer");
    } catch {
      /* swallow */
    }
  };

  return (
    <button
      type="button"
      onClick={handleTap}
      aria-label={`Open ${yourPick.title} at ${yourPick.source}`}
      style={{
        appearance: "none",
        textAlign: "left",
        width: "calc(100% - 0px)",
        margin: "12px 0 0",
        padding: 10,
        border: "0.5px solid #E8E2D9",
        borderRadius: 10,
        background: "#FFFCF8",
        display: "flex",
        alignItems: "center",
        gap: 10,
        cursor: "pointer",
        minHeight: 44,
      }}
    >
      <div
        style={{
          width: 44,
          height: 52,
          flexShrink: 0,
          background: "#F1EFE8",
          borderRadius: 6,
          overflow: "hidden",
        }}
      >
        {yourPick.thumbnail && (
          <img
            src={yourPick.thumbnail}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: "var(--font-display, var(--font-serif, serif))",
            fontStyle: "italic",
            fontSize: 11,
            color: isCaveat ? "#6B6560" : "#993C1D",
            margin: "0 0 2px",
          }}
        >
          {isCaveat ? "Closest we found" : "Best match we found"}
        </div>
        <div
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: "#2C2820",
            margin: "0 0 2px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {yourPick.title}
        </div>
        <div style={{ fontSize: 12, color: "#6B6560" }}>
          {yourPick.source ? `${yourPick.source} · ` : ""}
          {yourPick.price ?? ""}
        </div>
      </div>
      <ChevronRight size={18} color="#6B6560" />
    </button>
  );
}



/* ---------- Skeleton ---------- */

export function PurchaseConfidenceCardSkeleton() {
  return (
    <div
      className="mt-4 l2f-skeleton"
      style={{
        borderRadius: 18,
        height: 360,
        border: `0.5px solid ${MUTED_BORDER}`,
      }}
      aria-hidden
    />
  );
}

/* ---------- Main component ---------- */

export type PurchaseConfidenceCardProps = {
  heroItem: ConfidenceHeroItem | null;
  sizeProfile: SizeProfileLite;
  brandFitTops: BrandFitEntry[];
  brandFitBottoms: BrandFitEntry[];
  isGuest: boolean;
  guestGender?: Gender | null;
  detectedGender?: "men" | "women" | "unisex" | null;
  outfitId?: string | null;
  outfitCreatedAt?: string | null;
  /** Per-outfit explicit gender override (outfits.effective_gender). */
  effectiveGender?: "men" | "women" | "unisex" | "non-binary" | null;
  onGuestRegenerate?: (model: GuestModelProfile, size: string | null) => void;
  guestRegenStatus?: "idle" | "loading" | "success" | "error";
  guestRegenError?: string | null;
  onProfileUpdated?: () => void;
  /** Fires whenever the active tier changes (initial pick, auto-pick, user tap). */
  onTierChange?: (tier: TierName) => void;
};


export default function PurchaseConfidenceCard({
  heroItem,
  sizeProfile,
  brandFitTops,
  brandFitBottoms,
  isGuest,
  guestGender = null,
  detectedGender = null,
  outfitId = null,

  outfitCreatedAt = null,
  effectiveGender = null,
  onGuestRegenerate,
  guestRegenStatus = "idle",
  guestRegenError = null,
  onProfileUpdated,
  onTierChange,
}: PurchaseConfidenceCardProps) {



  const fit = useMemo(
    () => (heroItem ? getFitConfidence(heroItem, brandFitTops, brandFitBottoms, sizeProfile) : null),
    [heroItem, brandFitTops, brandFitBottoms, sizeProfile],
  );

  // F30.1 — removed `versatility`, `needItems`, `ownedItems` useMemos.
  // Versatility (CATEGORY_PAIRS / getVersatilityCount / Swatch chips) will be
  // rebuilt in F30.5 as a sibling card AFTER LB3. Spec: 3 sub-states
  // (empty wardrobe / N matches with up to 3 swatch chips / no matches).
  // Gap items ("What you need" / "You already own") deleted permanently —
  // duplicated by LB1 shopping list.


  // ---- Tier discovery ----
  const TIER_ORDER: TierName[] = ["budget", "mid", "premium"];
  const availableTiers = useMemo<TierName[]>(() => {
    const t = heroItem?.tiers;
    if (!t) return [];
    return TIER_ORDER.filter((name) => {
      const p = t[name] as ShoppingProduct | null | undefined;
      return !!(p && p.link && typeof p.link === "string" && p.link.startsWith("http"));
    });
  }, [heroItem]);

  // ---- Fit enrichment via fit-confidence edge function (multi-tier) ----
  type EnrichedFit = {
    confidence_level: "low" | "medium" | "medium-high" | "high";
    based_on: string[];
    limited_by: string[];
    positive_rows: string[];
    warning_rows: string[];
    recommended_size: string | null;
    recommended_size_note: string | null;
    data_sources?: {
      has_profile?: boolean;
      has_garment_data?: boolean;
      has_brand_fit_signal?: boolean;
      has_size_chart?: boolean;
      has_outcomes?: boolean;
      outcome_count?: number;
    };
    /** Set by fit-confidence when the chart convention requires a size we don't have. */
    missing_size_field?: "letter" | "number" | null;
    chart_convention?: "letter" | "numeric" | "mixed" | "unknown";
  };
  type TierConfidenceMap = Partial<Record<TierName, EnrichedFit>>;

  const [tierConfidence, setTierConfidence] = useState<TierConfidenceMap>({});
  const [enriching, setEnriching] = useState(false);
  const userId = (sizeProfile as { user_id?: string | null } | null)?.user_id ?? null;

  function pickDefaultTier(tc: TierConfidenceMap, available: TierName[]): TierName {
    const eligible = available.filter(
      (t) => tc[t] && ["high", "medium-high"].includes(tc[t]!.confidence_level),
    );
    if (eligible.length === 0) return available.includes("mid") ? "mid" : (available[0] ?? "mid");
    if (eligible.includes("mid")) return "mid";
    const high = eligible.find((t) => tc[t]!.confidence_level === "high");
    if (high) return high;
    return eligible[0];
  }

  const [activeTier, setActiveTier] = useState<TierName>(() =>
    availableTiers.includes("mid") ? "mid" : (availableTiers[0] ?? "mid"),
  );

  // Reset active tier when hero changes
  useEffect(() => {
    const fallback = availableTiers.includes("mid") ? "mid" : (availableTiers[0] ?? "mid");
    setActiveTier(fallback);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heroItem?.id]);

  // Mirror tier state to parent so the LB1 items list can flip with it.
  useEffect(() => {
    onTierChange?.(activeTier);
  }, [activeTier, onTierChange]);

  useEffect(() => {
    if (!heroItem?.id || !userId || isGuest || availableTiers.length === 0) return;
    let cancelled = false;
    setTierConfidence({});
    setEnriching(true);
    const tiersBody: Partial<Record<TierName, { product_url: string; retailer?: string | null }>> = {};
    for (const t of availableTiers) {
      const p = heroItem.tiers![t] as ShoppingProduct;
      tiersBody[t] = { product_url: p.link, retailer: p.source ?? null };
    }
    supabase.functions
      .invoke("fit-confidence", {
        body: {
          user_id: userId,
          hero_id: heroItem.id,
          tiers: tiersBody,
          item_category: heroItem.category ?? "top",
          effective_gender: effectiveGender ?? null,
        },
      })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (!error && data && !(data as { error?: string }).error) {
          const tc = data as TierConfidenceMap;
          setTierConfidence(tc);
          // Re-pick default tier based on real data
          setActiveTier((current) => {
            const pref = pickDefaultTier(tc, availableTiers);
            // Only auto-switch if user hasn't deviated from the initial fallback
            return current === "mid" || !availableTiers.includes(current) ? pref : current;
          });
        }
      })
      .finally(() => {
        if (!cancelled) setEnriching(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heroItem?.id, userId, isGuest, availableTiers.join(","), effectiveGender]);


  // ---- Guest garment data (display-only; no personalised confidence) ----
  type GuestGarmentRow = {
    product_url: string;
    fabric_composition: string | null;
    fit_type: string | null;
    size_chart: Record<string, Record<string, string | null>> | null;
    model_reference: {
      size_worn?: string | null;
      model_height?: string | null;
      model_chest?: string | null;
    } | null;
    fit_copy: string | null;
    data_confidence: string | null;
    scrape_status: string | null;
    gender: string | null;
    // A-7a PR 5c — structured stretch enum from garment_data. Optional on the
    // row type because older RPC responses may omit it; runtime coercion in
    // assessmentCtx handles missing/invalid values gracefully.
    fabric_stretch_enum?: "none" | "light" | "moderate" | "high" | null;
  };
  const [guestGarmentData, setGuestGarmentData] = useState<
    Partial<Record<TierName, GuestGarmentRow>>
  >({});
  const [sizeChartOpen, setSizeChartOpen] = useState(false);
  // Bumped whenever garment_data changes for any of the active hero's tier URLs
  // (via realtime subscription). Forces re-fetch + re-compute of assessment.
  const [garmentDataVersion, setGarmentDataVersion] = useState(0);

  useEffect(() => {
    setSizeChartOpen(false);
  }, [activeTier, heroItem?.id]);

  useEffect(() => {
    if (!heroItem?.id || availableTiers.length === 0) {
      setGuestGarmentData({});
      return;
    }
    let cancelled = false;
    const urlByTier: Partial<Record<TierName, string>> = {};
    const urls: string[] = [];
    for (const t of availableTiers) {
      const p = heroItem.tiers![t] as ShoppingProduct;
      if (p?.link) {
        urlByTier[t] = p.link;
        urls.push(p.link);
      }
    }
    if (urls.length === 0) return;
    setGuestGarmentData({});
    supabase
      .rpc("get_guest_garment_data", { p_product_urls: urls })
      .then(({ data, error }) => {
        if (cancelled || error || !data) return;
        const rows = data as GuestGarmentRow[];
        const byTier: Partial<Record<TierName, GuestGarmentRow>> = {};
        for (const t of availableTiers) {
          const url = urlByTier[t];
          if (!url) continue;
          const match = rows.find((r) => r.product_url === url);
          if (match) byTier[t] = match;
        }
        confidenceDebug("PurchaseConfidenceCard.get_guest_garment_data", {
          isGuest,
          activeTier,
          requestedUrls: urls,
          rows,
          activeRow: byTier[activeTier] ?? null,
        });
        setGuestGarmentData(byTier);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heroItem?.id, availableTiers.join(","), garmentDataVersion]);

  // Realtime: re-fetch garment_data when a scrape lands for any tier URL of the
  // current hero. Triggers the assessment re-compute via garmentDataVersion.
  useEffect(() => {
    if (!heroItem?.id) return;
    const tierUrls: string[] = [];
    for (const t of availableTiers) {
      const p = heroItem.tiers?.[t] as ShoppingProduct | null | undefined;
      if (p?.link) tierUrls.push(p.link);
    }
    if (tierUrls.length === 0) return;
    const urlSet = new Set(tierUrls);
    const channel = supabase
      .channel(`garment_data_${heroItem.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "garment_data" },
        (payload: { new?: { product_url?: string }; old?: { product_url?: string } }) => {
          const url = payload?.new?.product_url ?? payload?.old?.product_url ?? null;
          if (url && urlSet.has(url)) {
            console.log(`[confidence-card] garment_data updated for hero=${heroItem.id} url=${url}`);
            setGarmentDataVersion((v) => v + 1);
          }
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heroItem?.id, availableTiers.join(",")]);

  function sizeChartHasMeasurements(
    chart: Record<string, Record<string, string | null>> | null | undefined,
  ): boolean {
    if (!chart || typeof chart !== "object") return false;
    for (const sizeKey of Object.keys(chart)) {
      const row = chart[sizeKey];
      if (!row || typeof row !== "object") continue;
      for (const v of Object.values(row)) {
        if (v != null && String(v).trim() !== "" && String(v).toLowerCase() !== "null") {
          return true;
        }
      }
    }
    return false;
  }

  // Guest progressive profile — lifted so hero header can swap thumb for silhouette
  // once the guest has selected a height. Declared before any early return to
  // keep hook order stable.
  const [guestProfile, setGuestProfile] = useState<{
    size: string | null;
    sizeNumber: string | null;
    model: GuestModelProfile;
  }>(() => {
    // PR 4d — lazy hydration from sessionStorage so first render of the
    // assessmentCtx has user_size/body_shape/body_size for guest users.
    const stored = readGuestSizeFromStorage();
    return {
      size: stored.size,
      sizeNumber: stored.sizeNumber,
      model: readGuestModelProfile() ?? { height: null, body_shape: null, body_size: null },
    };
  });

  // PR 4d — sessionStorage gender fallback. When the parent's guestGender prop
  // hasn't resolved yet (detectedGender + guestSizeProfile both loading), pull
  // the gender persisted by GuestProgressiveFit's writeGuestSize so the brand
  // lookups in generateConfidenceAssessment resolve effectiveGender on first run.
  const guestGenderFromStorage = useMemo<Gender | null>(
    () => (typeof window === "undefined" ? null : readGuestSizeFromStorage().gender),
    // Re-read whenever guestProfile changes (covers the case where the user
    // first picks their size inside the card and we want to capture gender too).
    [guestProfile.size, guestProfile.sizeNumber],
  );


  // ---- Recommendation (Build 2) -----------------------------------------
  const activeGarment = guestGarmentData[activeTier] ?? null;
  const sp = (sizeProfile ?? {}) as Record<string, unknown>;
  const authSize =
    (sp.size_top_letter as string | null | undefined) ??
    (sp.size_letter as string | null | undefined) ??
    null;
  const authSizeNumber =
    (sp.size_top_uk_number as string | null | undefined) ?? null;
  const authGender = (() => {
    const raw =
      (sp.model_gender as string | null | undefined) ??
      (sp.size_gender as string | null | undefined) ??
      null;
    // Tolerate "woman"/"man"/"female" etc. so brand-table lookups don't miss.
    const normalised = normaliseGender(raw);
    if (normalised) return normalised as Gender;
    if (raw === "non-binary") return "non-binary" as Gender;
    return null;
  })();

  // Optimistic verdicts captured via the inline brand-fit re-prompt so the
  // assessment recomputes immediately without waiting for the parent refetch.
  const [localBrandFitAdditions, setLocalBrandFitAdditions] = useState<{
    tops: UserBrandFitEntry[];
    bottoms: UserBrandFitEntry[];
  }>({ tops: [], bottoms: [] });

  // PR 6c.2-2 — brand intent fetched alongside created_at below; declared
  // here so the assessmentCtx memo can read it.
  const [userIntentRetailer, setUserIntentRetailer] = useState<string | null>(null);

  // Reset optimistic additions when the parent props refresh.
  useEffect(() => {
    setLocalBrandFitAdditions({ tops: [], bottoms: [] });
  }, [brandFitTops, brandFitBottoms]);

  const effectiveBrandFitTops = useMemo(
    () => [
      ...((brandFitTops ?? []) as unknown as UserBrandFitEntry[]),
      ...localBrandFitAdditions.tops,
    ],
    [brandFitTops, localBrandFitAdditions.tops],
  );
  const effectiveBrandFitBottoms = useMemo(
    () => [
      ...((brandFitBottoms ?? []) as unknown as UserBrandFitEntry[]),
      ...localBrandFitAdditions.bottoms,
    ],
    [brandFitBottoms, localBrandFitAdditions.bottoms],
  );

  const assessmentCtx: ConfidenceContext = useMemo(
    () => ({
      fit_type: activeGarment?.fit_type ?? null,
      fabric_composition: activeGarment?.fabric_composition ?? null,
      category: heroItem?.category ?? null,
      size_chart: (activeGarment?.size_chart as Record<string, unknown> | null) ?? null,
      fit_copy: activeGarment?.fit_copy ?? null,
      model_reference: activeGarment?.model_reference
        ? {
            size_worn: activeGarment.model_reference.size_worn ?? null,
            model_height: activeGarment.model_reference.model_height ?? null,
          }
        : null,
      // PR 6c.2-2 — user-stated brand (from text_description) wins over
      // product source. SerpApi may return PrettyLittleThing when the user
      // typed "H&M dress" — brand-aware fit should still apply H&M.
      retailer:
        userIntentRetailer ??
        (heroItem?.tiers?.[activeTier] as ShoppingProduct | null | undefined)?.source ??
        null,
      user_size: isGuest ? guestProfile.size : authSize,
      user_size_uk_number: isGuest ? guestProfile.sizeNumber : authSizeNumber,
      user_height: isGuest
        ? guestProfile.model.height
        : ((sp.model_height as string | null | undefined) ?? null),
      user_body_shape: isGuest
        ? guestProfile.model.body_shape
        : ((sp.model_body_shape as string | null | undefined) ?? null),
      user_body_size: isGuest
        ? guestProfile.model.body_size
        : ((sp.model_body_size as string | null | undefined) ?? null),
      user_gender: isGuest ? (guestGender ?? guestGenderFromStorage) : authGender,
      garment_gender: (activeGarment?.gender ?? null) as
        | "men"
        | "women"
        | "unisex"
        | null,
      gender_override: effectiveGender,
      user_brand_fit_tops: effectiveBrandFitTops,
      user_brand_fit_bottoms: effectiveBrandFitBottoms,
      // Edge-fn per-tier signal (auth users only; null for guests / before enrichment).
      edge_tier_signal: tierConfidence?.[activeTier] ?? null,
      // A-7a PR 5c — structured attrs (7 from detected_items hero + stretch
      // enum from garment_data). Pass-through only; T2_GARMENT_ATTRIBUTES
      // scoring tier is not active yet.
      silhouette: heroItem?.silhouette ?? null,
      length: heroItem?.length ?? null,
      neckline: heroItem?.neckline ?? null,
      sleeve_length: heroItem?.sleeve_length ?? null,
      fit: heroItem?.fit ?? null,
      fabric_weight: heroItem?.fabric_weight ?? null,
      structure: heroItem?.structure ?? null,
      fabric_stretch_enum: activeGarment?.fabric_stretch_enum ?? null,
      // A-7b PR 6a — item name for {item} interpolation in styling row.
      // Primary: detected_items.name (heroItem.name). Secondary fallback to
      // garment_data.product_name not yet plumbed via get_guest_garment_data.
      item_name: heroItem?.name ?? null,

    }),
    [
      activeGarment,
      heroItem?.category,
      heroItem?.tiers,
      heroItem?.silhouette,
      heroItem?.length,
      heroItem?.neckline,
      heroItem?.sleeve_length,
      heroItem?.fit,
      heroItem?.fabric_weight,
      heroItem?.structure,
      heroItem?.name,

      activeTier,
      isGuest,
      guestProfile,
      guestGender,
      guestGenderFromStorage,
      authSize,
      authSizeNumber,
      authGender,
      effectiveBrandFitTops,
      effectiveBrandFitBottoms,
      effectiveGender,
      sp.model_height,
      sp.model_body_shape,
      sp.model_body_size,
      tierConfidence,
      userIntentRetailer,
    ],
  );

  useEffect(() => {
    if (!shouldLogConfidenceDebug()) return;
    let storedGuestSizeProfile: unknown = null;
    try {
      const stored = window.sessionStorage.getItem("l2f_guest_size");
      storedGuestSizeProfile = stored ? JSON.parse(stored) : null;
    } catch (e) {
      storedGuestSizeProfile = { parse_error: e instanceof Error ? e.message : String(e) };
    }
    confidenceDebug("PurchaseConfidenceCard.assessmentCtx", {
      isGuest,
      activeTier,
      user_gender: assessmentCtx.user_gender ?? null,
      retailer_canonical: assessmentCtx.retailer
        ? retailerIdFromLink(assessmentCtx.retailer) ?? assessmentCtx.retailer.toLowerCase()
        : null,
      retailer_raw: assessmentCtx.retailer ?? null,
      detectedGender,
      guestGender,
      effectiveGender,
      guestSizeProfile: storedGuestSizeProfile,
      activeGarment,
      assessmentCtx,
    });
  }, [
    activeGarment,
    activeTier,
    assessmentCtx,
    detectedGender,
    effectiveGender,
    guestGender,
    isGuest,
  ]);



  const [assessment, setAssessment] = useState<ConfidenceAssessment | null>(null);
  const [isComputing, setIsComputing] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setIsComputing(true);
    generateConfidenceAssessment(assessmentCtx, supabase)
      .then((res) => {
        if (!cancelled) setAssessment(res);
      })
      .catch((e) => {
        console.warn("[PurchaseConfidenceCard] assessment failed", e);
      })
      .finally(() => {
        if (!cancelled) setIsComputing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [assessmentCtx]);

  const recommendation = assessment;

  // PQ-5d — Pairs with + Fills a gap lens data. Authed users only.
  // Fetches LB3 outcome (from outfits row) and active+eligible wardrobe
  // items (from wardrobe_items). Guests skip; lens system surfaces a
  // signup nudge instead.
  type Lb3Fetched = {
    lb3_outcome: string | null;
    lb3_owned_items: Array<{ name?: string | null; category?: string | null }>;
    wardrobe_total: number | null;
  };
  const [lb3Data, setLb3Data] = useState<Lb3Fetched | null>(null);
  const [wardrobeItems, setWardrobeItems] = useState<
    Array<{
      category: string | null;
      material: string | null;
      formality: string | null;
      sleeve_length: string | null;
      style_tags: string[] | null;
      occasion_tags: string[] | null;
      name: string | null;
    }>
  >([]);
  const [wardrobeFetched, setWardrobeFetched] = useState(false);

  useEffect(() => {
    if (isGuest || !userId) {
      setLb3Data(null);
      setWardrobeItems([]);
      setWardrobeFetched(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [outfitRes, wardrobeRes] = await Promise.all([
          outfitId
            ? supabase
                .from("outfits")
                .select("lb3_outcome, lb3_owned_items, lb3_match_meta")
                .eq("id", outfitId)
                .maybeSingle()
            : Promise.resolve({ data: null }),
          supabase
            .from("wardrobe_items")
            .select(
              "category, material, formality, sleeve_length, style_tags, occasion_tags, name",
            )
            .eq("user_id", userId)
            .eq("is_active", true)
            .eq("lb3_eligible", true),
        ]);
        if (cancelled) return;
        const items = (wardrobeRes as { data: typeof wardrobeItems | null }).data ?? [];
        setWardrobeItems(items);
        setWardrobeFetched(true);
        const orow = (outfitRes as { data: Record<string, unknown> | null }).data;
        if (orow) {
          const meta = orow.lb3_match_meta as { wardrobe_total?: number } | null;
          setLb3Data({
            lb3_outcome: (orow.lb3_outcome as string | null) ?? null,
            lb3_owned_items: Array.isArray(orow.lb3_owned_items)
              ? (orow.lb3_owned_items as Array<{ name?: string; category?: string }>)
              : [],
            wardrobe_total: meta?.wardrobe_total ?? items.length,
          });
        } else {
          setLb3Data(null);
        }
      } catch {
        if (!cancelled) {
          setWardrobeFetched(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isGuest, userId, outfitId]);

  // PQ-5d — Compute new lenses + merge into the assessment's lens system.
  const lensGender = useMemo<TaxonomyGender | null>(() => {
    const raw =
      (effectiveGender as string | null) ??
      (isGuest ? (guestGender ?? guestGenderFromStorage) : authGender);
    if (raw === "women") return "women";
    if (raw === "men") return "men";
    if (raw === "non-binary" || raw === "non_binary") return "non_binary";
    return null;
  }, [effectiveGender, isGuest, guestGender, guestGenderFromStorage, authGender]);

  const mergedLenses = useMemo<LensSystemType | null>(() => {
    const base = recommendation?.lenses ?? null;
    if (!base) return null;
    if (isGuest) {
      // Guest: leave pairs_with / fills_gap null; nudge handles the gap.
      return base;
    }
    const pairs_with = computePairsWithLens(
      lb3Data?.lb3_outcome ?? null,
      lb3Data?.lb3_owned_items ?? null,
      lb3Data?.wardrobe_total ?? wardrobeItems.length,
    );
    const fills_gap = computeFillsGapLens(
      heroItem
        ? {
            category: heroItem.category ?? null,
            sleeve_length: heroItem.sleeve_length ?? null,
            silhouette: heroItem.silhouette ?? null,
            length: heroItem.length ?? null,
            name: heroItem.name ?? null,
          }
        : null,
      wardrobeItems,
      lensGender,
    );
    return { ...base, pairs_with, fills_gap };
  }, [recommendation?.lenses, isGuest, lb3Data, wardrobeItems, heroItem, lensGender]);

  const wardrobeItemCount = wardrobeFetched ? wardrobeItems.length : null;

  const handleLensSignupNudgeTap = () => {
    setSignupTrigger("lens_signup_nudge");
    trackEvent("lens_signup_nudge_tapped", {
      outfit_id: outfitId,
      is_guest: true,
    });
  };

  const handleAddWardrobeNudgeTap = () => {
    trackEvent("lens_sparse_wardrobe_nudge_tapped", {
      outfit_id: outfitId,
      wardrobe_item_count: wardrobeItemCount,
    });
  };



  // A-7b PR 6c-3 — LLM styling-note fallback. Fires only when computeFitScore
  // flagged `styling_pending=true` (no hardcoded pattern matched but inputs
  // look rich enough). Non-blocking: card renders immediately; sparkle row
  // appears later (or never) via re-render.
  const llmEnabled =
    recommendation?.styling_pending === true && !!recommendation?.styling_llm_payload;
  const llmCacheKey = useMemo(() => {
    if (!recommendation?.styling_llm_payload) return null;
    return JSON.stringify(recommendation.styling_llm_payload);
  }, [recommendation?.styling_llm_payload]);
  const { stylingCopy: llmStylingCopy } = useStylingNoteLLM({
    enabled: llmEnabled,
    payload: recommendation?.styling_llm_payload ?? null,
    cacheKey: llmCacheKey,
  });


  // ---- On-demand scrape UX: subtext + pulse on upgrade ------------------
  // Outfit created_at: prefer prop; fall back to a one-shot fetch by outfitId
  // so the subtext window doesn't depend on plumbing changes upstream.
  const [fetchedCreatedAt, setFetchedCreatedAt] = useState<string | null>(null);
  // PR 6c.2-2 — text-flow brand intent state declared earlier (see comment
  // above the assessmentCtx memo). This effect fetches it alongside
  // created_at in a single round-trip.
  useEffect(() => {
    if (!outfitId) return;
    if (outfitCreatedAt && userIntentRetailer !== null) return;
    let cancelled = false;
    void (async () => {
      try {
        const { data } = await supabase
          .from("outfits")
          .select("created_at, user_intent_retailer")
          .eq("id", outfitId)
          .maybeSingle();
        if (cancelled || !data) return;
        if (!outfitCreatedAt && data.created_at) {
          setFetchedCreatedAt(data.created_at as string);
        }
        const intent = (data as { user_intent_retailer?: string | null }).user_intent_retailer ?? null;
        if (intent) setUserIntentRetailer(intent);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [outfitId, outfitCreatedAt, userIntentRetailer]);
  const effectiveCreatedAt = outfitCreatedAt ?? fetchedCreatedAt;
  const isRecentlyAnalysed = useMemo(() => {
    if (!effectiveCreatedAt) return false;
    const ageSec = (Date.now() - new Date(effectiveCreatedAt).getTime()) / 1000;
    return ageSec < 60;
  }, [effectiveCreatedAt]);
  const [recentWindowOpen, setRecentWindowOpen] = useState(false);
  useEffect(() => {
    if (!isRecentlyAnalysed || !effectiveCreatedAt) {
      setRecentWindowOpen(false);
      return;
    }
    setRecentWindowOpen(true);
    const remainingMs = Math.max(
      0,
      60_000 - (Date.now() - new Date(effectiveCreatedAt).getTime()),
    );
    const t = setTimeout(() => setRecentWindowOpen(false), remainingMs);
    return () => clearTimeout(t);
  }, [isRecentlyAnalysed, effectiveCreatedAt]);

  const hasGarmentDataForActiveTier = !!activeGarment;
  const showScrapePendingSubtext =
    !!recommendation &&
    recommendation.recommendation_type === "hedged" &&
    !hasGarmentDataForActiveTier &&
    recentWindowOpen;

  // Fire analytics: subtext shown once per (outfit, tier).
  const pendingShownKeysRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!showScrapePendingSubtext || !outfitId) return;
    const key = `${outfitId}::${activeTier}`;
    if (pendingShownKeysRef.current.has(key)) return;
    pendingShownKeysRef.current.add(key);
    trackEvent("confidence_scrape_pending_shown", {
      outfit_id: outfitId,
      hero_category: heroItem?.category ?? null,
      tier_active: activeTier,
    });
  }, [showScrapePendingSubtext, outfitId, activeTier, heroItem?.category]);

  // Fire analytics: timeout (subtext window closed without upgrade).
  const timeoutFiredRef = useRef<Set<string>>(new Set());
  const wasHedgedDuringWindowRef = useRef(false);
  useEffect(() => {
    if (showScrapePendingSubtext) wasHedgedDuringWindowRef.current = true;
  }, [showScrapePendingSubtext]);
  useEffect(() => {
    if (recentWindowOpen || !outfitId) return;
    if (!wasHedgedDuringWindowRef.current) return;
    if (!recommendation || recommendation.recommendation_type !== "hedged") return;
    const key = `${outfitId}::${activeTier}`;
    if (timeoutFiredRef.current.has(key)) return;
    timeoutFiredRef.current.add(key);
    trackEvent("confidence_scrape_timeout", {
      outfit_id: outfitId,
      hero_category: heroItem?.category ?? null,
    });
  }, [recentWindowOpen, outfitId, activeTier, recommendation, heroItem?.category]);

  // Pulse: 1.5s subtle background tint when recommendation upgrades from hedged.
  const [shouldPulse, setShouldPulse] = useState(false);
  const prevRecTypeRef = useRef(recommendation?.recommendation_type);
  useEffect(() => {
    const prev = prevRecTypeRef.current;
    const next = recommendation?.recommendation_type;
    if (
      prev === "hedged" &&
      next &&
      next !== "hedged" &&
      next !== "prompt_user"
    ) {
      setShouldPulse(true);
      if (outfitId) {
        const elapsedSec = outfitCreatedAt
          ? (Date.now() - new Date(outfitCreatedAt).getTime()) / 1000
          : null;
        trackEvent("confidence_recommendation_upgraded", {
          outfit_id: outfitId,
          hero_category: heroItem?.category ?? null,
          tier_active: activeTier,
          from_type: "hedged",
          to_type: next,
          elapsed_seconds_from_analysis: elapsedSec,
        });
      }
      const t = setTimeout(() => setShouldPulse(false), 1500);
      prevRecTypeRef.current = next;
      return () => clearTimeout(t);
    }
    prevRecTypeRef.current = next;
  }, [recommendation?.recommendation_type, outfitId, outfitCreatedAt, activeTier, heroItem?.category]);




  // Counter that GuestProgressiveFit watches to revert + scroll to a step
  // when the diagnostic message is tapped.
  const [focusFieldRequest, setFocusFieldRequest] = useState<
    { field: MissingField["field"]; nonce: number } | null
  >(null);

  function handleDiagnosticClick() {
    const mf = recommendation?.missing_fields?.[0];
    if (!mf) return;
    if (isGuest) {
      setFocusFieldRequest({ field: mf.field, nonce: Date.now() });
    } else {
      const section: AccountFocusSection =
        mf.field === "size" ? "size" : "model";
      requestAccountFocus(section);
    }
  }


  // ---- Brand fit re-prompt (auth-only, default brands, no existing verdict) ----
  type RepromptCategory = "tops" | "bottoms";
  type RepromptVerdict = "true_to_size" | "runs_small" | "runs_large";

  const heroCategoryLower = (heroItem?.category ?? "").toLowerCase();
  const repromptCategory: RepromptCategory | null =
    heroCategoryLower === "top" || heroCategoryLower === "outerwear"
      ? "tops"
      : heroCategoryLower === "bottom"
        ? "bottoms"
        : null;

  const activeSourceRaw =
    (heroItem?.tiers?.[activeTier] as ShoppingProduct | null | undefined)?.source ?? null;
  const canonicalRetailer = normaliseBrand(activeSourceRaw);

  const [dismissedPrompts, setDismissedPrompts] = useState<string[]>([]);
  const [dismissedLoaded, setDismissedLoaded] = useState(false);
  useEffect(() => {
    if (!userId || isGuest) {
      setDismissedPrompts([]);
      setDismissedLoaded(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await (supabase.from("profiles") as never as {
          select: (cols: string) => {
            eq: (k: string, v: string) => {
              maybeSingle: () => Promise<{ data: { brand_fit_prompts_dismissed?: unknown } | null }>;
            };
          };
        })
          .select("brand_fit_prompts_dismissed")
          .eq("id", userId)
          .maybeSingle();
        if (cancelled) return;
        const raw = data?.brand_fit_prompts_dismissed;
        setDismissedPrompts(Array.isArray(raw) ? (raw as string[]) : []);
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setDismissedLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [userId, isGuest]);

  const userVerdictForThis = useMemo(() => {
    if (!canonicalRetailer || !repromptCategory) return null;
    const list = repromptCategory === "tops" ? effectiveBrandFitTops : effectiveBrandFitBottoms;
    return (
      list.find((v) => {
        const stored = normaliseBrand((v as { brand?: string; retailer?: string }).brand ?? (v as { retailer?: string }).retailer ?? "");
        return stored && stored === canonicalRetailer;
      }) ?? null
    );
  }, [canonicalRetailer, repromptCategory, effectiveBrandFitTops, effectiveBrandFitBottoms]);

  const isDismissedForBrand =
    !!canonicalRetailer &&
    (dismissedPrompts.includes(canonicalRetailer) || dismissedPrompts.includes(DISMISS_ALL_SENTINEL));

  const shouldShowReprompt =
    !isGuest &&
    !!userId &&
    dismissedLoaded &&
    !!canonicalRetailer &&
    !!repromptCategory &&
    isInDefaultBrands(canonicalRetailer, authGender) &&
    !userVerdictForThis &&
    !isDismissedForBrand;

  // Fire `brand_fit_reprompt_shown` once per (outfit, retailer, category).
  const repromptShownKeysRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!shouldShowReprompt) return;
    const key = `${outfitId ?? ""}::${canonicalRetailer}::${repromptCategory}`;
    if (repromptShownKeysRef.current.has(key)) return;
    repromptShownKeysRef.current.add(key);
    trackEvent("brand_fit_reprompt_shown", {
      user_id: userId,
      outfit_id: outfitId,
      retailer: canonicalRetailer,
      category: repromptCategory,
    });
  }, [shouldShowReprompt, outfitId, canonicalRetailer, repromptCategory, userId]);

  const [savingVerdict, setSavingVerdict] = useState<RepromptVerdict | null>(null);

  async function handleVerdictTap(verdict: RepromptVerdict) {
    if (!userId || !canonicalRetailer || !repromptCategory) return;
    setSavingVerdict(verdict);

    const targetColumn =
      repromptCategory === "tops" ? "brand_fit_tops" : "brand_fit_bottoms";
    const existingArray = (repromptCategory === "tops"
      ? (brandFitTops ?? [])
      : (brandFitBottoms ?? [])) as UserBrandFitEntry[];

    const newEntry = {
      brand: canonicalRetailer,
      verdict,
      category: repromptCategory,
      source: "results_page_reprompt",
      last_updated: new Date().toISOString().split("T")[0],
    } as unknown as UserBrandFitEntry;

    // Optimistic
    setLocalBrandFitAdditions((prev) => ({
      ...prev,
      [repromptCategory]: [...prev[repromptCategory], newEntry],
    }));

    const updated = [...existingArray, newEntry];
    const { error } = await (supabase.from("profiles") as never as {
      update: (v: Record<string, unknown>) => { eq: (k: string, v: string) => Promise<{ error: { message: string } | null }> };
    })
      .update({ [targetColumn]: updated })
      .eq("id", userId);

    setSavingVerdict(null);

    if (error) {
      // Revert
      setLocalBrandFitAdditions((prev) => ({
        ...prev,
        [repromptCategory]: prev[repromptCategory].filter((e) => e !== newEntry),
      }));
      toast.error("Couldn't save — try again");
      return;
    }

    trackEvent("brand_fit_reprompt_verdict_saved", {
      user_id: userId,
      outfit_id: outfitId,
      retailer: canonicalRetailer,
      category: repromptCategory,
      verdict,
    });
    toast.success(`Saved — ${canonicalRetailer} ${verdict.replace(/_/g, " ")}`);
    onProfileUpdated?.();
  }

  const [dismissing, setDismissing] = useState(false);
  async function handleDismissReprompt() {
    if (!userId || !canonicalRetailer || !repromptCategory) return;
    if (dismissedPrompts.includes(canonicalRetailer)) return;
    setDismissing(true);
    const updated = [...dismissedPrompts, canonicalRetailer];
    // Optimistic
    setDismissedPrompts(updated);
    const { error } = await (supabase.from("profiles") as never as {
      update: (v: Record<string, unknown>) => { eq: (k: string, v: string) => Promise<{ error: { message: string } | null }> };
    })
      .update({ brand_fit_prompts_dismissed: updated })
      .eq("id", userId);
    setDismissing(false);
    if (error) {
      setDismissedPrompts(dismissedPrompts);
      toast.error("Couldn't dismiss — try again");
      return;
    }
    trackEvent("brand_fit_reprompt_dismissed", {
      user_id: userId,
      outfit_id: outfitId,
      retailer: canonicalRetailer,
      category: repromptCategory,
    });
    onProfileUpdated?.();
  }




  // Fire analytics once per shown recommendation (per outfit + tier + type/confidence).
  useEffect(() => {
    if (!heroItem?.id || !recommendation) return;
    trackEvent("confidence_card_recommendation_shown", {
      outfit_id: outfitId,
      user_type: isGuest ? "guest" : "auth",
      recommendation_type: recommendation.recommendation_type,
      confidence_level: recommendation.confidence_level,
      has_size_chart: !!assessmentCtx.size_chart,
      has_fit_copy: !!assessmentCtx.fit_copy,
      active_tier: activeTier,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    heroItem?.id,
    activeTier,
    recommendation?.recommendation_type,
    recommendation?.confidence_level,
  ]);

  // PQ-5a + SKIP-FLOOR-1 — derive verdict, flooring Skip when no
  // fit-predictive tier fired (avoids false negatives on sparse brand data).
  const verdict = useMemo<Verdict | null>(() => {
    const score = recommendation?.confidence_score;
    if (typeof score !== "number") return null;
    const tiers = (recommendation?.tiers_fired ?? []) as TierTag[];
    return verdictFromAssessment(score, tiers);
  }, [recommendation?.confidence_score, recommendation?.tiers_fired]);

  // PQ-5a-fix — styling note for 2nd verdict reason line (Option 2).
  // Source: existing sparkle row in reasoning_rows OR async LLM fallback copy.
  // Strip leading ✦ from source — the sparkle glyph comes from the dedicated span.
  const stylingNoteCopy = useMemo<string | null>(() => {
    const sparkleRow = recommendation?.reasoning_rows?.find((r) => r.icon === "sparkle");
    const raw = sparkleRow?.text ?? llmStylingCopy ?? null;
    if (!raw) return null;
    const trimmed = raw.replace(/^✦\s*/, "").trim();
    return trimmed.length > 0 ? trimmed : null;
  }, [recommendation?.reasoning_rows, llmStylingCopy]);

  // PQ-5a — results_viewed fires once per outfit view (guarded against
  // double-fire on re-renders; ref resets when outfit_id changes).
  const resultsViewedFiredRef = useRef<string | null>(null);
  useEffect(() => {
    if (!verdict || !recommendation || !outfitId) return;
    if (resultsViewedFiredRef.current === outfitId) return;
    resultsViewedFiredRef.current = outfitId;
    trackEvent("results_viewed", {
      outfit_id: outfitId,
      confidence_score: recommendation.confidence_score,
      confidence_level: recommendation.confidence_level,
      verdict,
      is_guest: isGuest,
    });
  }, [verdict, recommendation, outfitId, isGuest]);

  // PQ-5f — verdict_rendered fires once per PCC mount with full lens payload.
  // Captures rendered state for post-hoc tone QA + Brief 4 verification.
  const verdictRenderedFiredRef = useRef(false);
  useEffect(() => {
    if (!verdict || !recommendation) return;
    if (verdictRenderedFiredRef.current) return;
    verdictRenderedFiredRef.current = true;
    const lenses = recommendation.lenses ?? null;
    trackEvent("verdict_rendered", {
      outfit_id: outfitId,
      verdict,
      confidence_score: recommendation.confidence_score,
      tiers_fired: recommendation.tiers_fired ?? [],
      fit_lens_read: lenses?.fit?.read ?? null,
      fit_lens_copy: lenses?.fit?.copy ?? null,
      suits_you_present: !!lenses?.suits_you,
      pairs_with_read: lenses?.pairs_with?.read ?? null,
      fills_gap_present: !!lenses?.fills_gap,
      is_guest: isGuest,
    });
  }, [verdict, recommendation, outfitId, isGuest]);

  // PQ-5e — accordion state for "See alternatives" tier chip disclosure.
  // Collapsed by default; resets on unmount (no sessionStorage persistence).
  const [expanded, setExpanded] = useState(false);
  const expandedFiredRef = useRef(false);

  // WAVE-3A — auto-scroll on chevron expand + alternative tier tap.
  // cardRef pins card top into view when accordion opens; tierListRef
  // no-ops when the tier list is already visible (block: "nearest").
  const cardRef = useRef<HTMLElement | null>(null);
  const tierListRef = useRef<HTMLDivElement | null>(null);
  const didMountRef = useRef(false);

  useEffect(() => {
    if (expanded) {
      cardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [expanded]);

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    tierListRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [activeTier]);

  const handleToggleExpanded = useCallback(() => {
    setExpanded((prev) => {
      const next = !prev;
      if (next && !expandedFiredRef.current) {
        expandedFiredRef.current = true;
        trackEvent("tier_alternatives_expanded", {
          outfit_id: outfitId,
          verdict,
          is_guest: isGuest,
          tier_count: availableTiers.length,
        });
      }
      return next;
    });
  }, [outfitId, verdict, isGuest, availableTiers.length]);

  if (!heroItem) return null;


  const activeProduct =
    (heroItem.tiers?.[activeTier] as ShoppingProduct | null | undefined) ?? null;
  const heroPrice = activeProduct?.price ?? heroItem.tiers?.mid?.price ?? null;
  const heroDisplayName = activeProduct?.title ?? heroItem.name;

  // BEST-VISUAL-MATCH-1 — priceLineCopy retired. The inline "Your pick" row
  // below The Read now surfaces retailer + price (and any caveat). The legacy
  // "Mid · around £45" sub-line was duplicative once Your pick shipped.

  // PQ-5c — when the assessment carries a lens system, switch to the new
  // architecture: verdict block is lean (eyebrow + word + price), and
  // "THE READ" renders the lens system. When absent (pre-PQ-5c data),
  // fall back to the legacy path: reason line + ✦ in verdict block and
  // reasoning_rows render in "THE READ".
  const useNewArchitecture = !!recommendation?.lenses;

  // PQ-5f — gate "THE READ" eyebrow on content presence. Prevents a 26px
  // orphan when both lens system and legacy reasoning_rows have nothing
  // to render.
  const legacyHasReadContent =
    (recommendation?.reasoning_rows?.length ?? 0) > 0 ||
    !!llmStylingCopy ||
    !!recommendation?.diagnostic_message;
  const hasReadContent = useNewArchitecture
    ? !!(mergedLenses ?? recommendation?.lenses) || !!recommendation?.diagnostic_message
    : legacyHasReadContent;


  // Show the chip row even with a single tier so the user sees the pin
  // badge / tier label. Hidden only when there are no tiers at all.
  const showChips = availableTiers.length >= 1;

  // BEST-VISUAL-MATCH-2 — accordion shows only Cheaper + Premium, AND
  // dedups any tier whose link equals tiers.your_pick.link (avoids the
  // same item rendering twice: once inline above, once as a chip below).
  const yourPickLink =
    (heroItem?.tiers?.your_pick as ShoppingProduct | null | undefined)?.link ?? null;
  const accordionAltTiers = availableTiers.filter((t) => {
    if (t === "mid") return false;
    const p = heroItem?.tiers?.[t] as ShoppingProduct | null | undefined;
    if (yourPickLink && p?.link && p.link === yourPickLink) return false;
    return true;
  });


  return (
    <section
      ref={cardRef}
      aria-label="Purchase confidence"
      style={{
        position: "relative",
        borderRadius: 20,
        background: "#FFFCF8",
        border: "0.5px solid #E8E2D9",
        margin: "0 0 12px",
        padding: 14,
      }}
    >
      {/* Wave 3B — Commit 3j: offline-shopping badge removed from PCC.
          The decision surface is the shopping list (LB1/LB2/LB3). */}

      {/* PQ-5a — VERDICT HERO BLOCK (eyebrow + verdict word + price line). PQ-5e removed the 3-dot meter. */}
      {verdict && (
        <div
          aria-label="Verdict"
          style={{
            padding: "18px 8px 16px",
            textAlign: "center",
            borderBottom: "0.5px solid #E8E0D5",
            marginBottom: 14,
          }}
        >
          {(() => {
            const cleanedTitle = cleanProductTitle(heroDisplayName);
            if (!cleanedTitle) return null;
            return (
              <p
                style={{
                  fontSize: 11,
                  fontWeight: 500,
                  letterSpacing: "0.08em",
                  color: "#6B6560",
                  margin: "0 0 8px",
                  textTransform: "uppercase",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                }}
              >
                {cleanedTitle}
              </p>
            );
          })()}
          <p
            style={{
              fontFamily: "var(--font-display, var(--font-serif, serif))",
              fontStyle: "italic",
              fontSize: 32,
              fontWeight: 400,
              color: "#2C2820",
              margin: 0,
              lineHeight: 1,
              letterSpacing: "-0.5px",
            }}
          >
            {VERDICT_COPY[verdict]}
          </p>
          {/* PQ-5c — reason line and ✦ styling note moved into the lens
              system below. Rendered here only as backward-compat fallback
              for pre-PQ-5c assessments (no `lenses` field). */}
          {!useNewArchitecture && recommendation?.reasoning && (
            <p
              style={{
                fontFamily: "var(--font-display, var(--font-serif, serif))",
                fontStyle: "italic",
                fontSize: 15,
                fontWeight: 400,
                color: "#4A4540",
                margin: "10px 0 0",
                lineHeight: 1.4,
              }}
            >
              {recommendation.reasoning}
            </p>
          )}
          {!useNewArchitecture && stylingNoteCopy && verdict !== "skip" && (
            <p
              style={{
                fontFamily: "var(--font-display, var(--font-serif, serif))",
                fontStyle: "italic",
                fontSize: 13,
                fontWeight: 400,
                color: "#534AB7",
                margin: "6px 0 0",
                lineHeight: 1.4,
              }}
            >
              <span aria-hidden style={{ marginRight: 2 }}>✦</span>
              {stylingNoteCopy}
            </p>
          )}

          {/* PQ-5e — 3-dot confidence meter removed. Verdict word + lens
              system below carry the confidence signal. */}

          {/* BEST-VISUAL-MATCH-1 — price/retailer sub-line removed.
              The "Your pick" inline row below The Read now carries this. */}
        </div>
      )}

      {/* PQ-5b — THE READ (interim: existing A-7b content renders below until
          PQ-5c lens system replaces the body). Merchandising chip subsection
          removed; hero identity now lives as eyebrow above the verdict word. */}
      {verdict && hasReadContent && (
        <p
          style={{
            fontSize: 13,
            fontWeight: 500,
            letterSpacing: "0.08em",
            color: "#6B6560",
            margin: "0 0 12px",
            textTransform: "uppercase",
          }}
        >
          The read
        </p>
      )}


      {/* PQ-5c — when the assessment carries a lens system, render the
          lens rows here as the body of "THE READ". When absent, fall back
          to the legacy recommendation_text + reasoning_rows render below. */}
      {useNewArchitecture && recommendation?.lenses && (
        <>
          <LensSystem
            lenses={mergedLenses ?? recommendation.lenses}
            isGuest={isGuest}
            wardrobeItemCount={wardrobeItemCount}
            onSignupTapped={handleLensSignupNudgeTap}
            onAddWardrobeTapped={handleAddWardrobeNudgeTap}
          />
          {recommendation?.diagnostic_message && (
            <button
              type="button"
              onClick={
                recommendation.missing_fields.length > 0
                  ? handleDiagnosticClick
                  : undefined
              }
              disabled={recommendation.missing_fields.length === 0}
              className="l2f-type-caption"
              style={{
                marginTop: 4,
                marginBottom: 8,
                padding: "12px 8px",
                minHeight: 44,
                background: "transparent",
                border: "none",
                textAlign: "left",
                display: "block",
                width: "100%",
                cursor:
                  recommendation.missing_fields.length > 0 ? "pointer" : "default",
                color:
                  recommendation.missing_fields.length > 0 ? CORAL : "#9B958E",
                textDecoration:
                  recommendation.missing_fields.length > 0 ? "underline" : "none",
                lineHeight: 1.4,
              }}
            >
              <span>{recommendation.diagnostic_message}</span>
              {recommendation.missing_fields.length > 0 && <span> →</span>}
            </button>
          )}
        </>
      )}


      {/* RECOMMENDATION — PQ-5a-fix: demoted. Backward-compat fallback for
          pre-PQ-5c assessments (no `lenses` field). Skipped under new
          architecture (lens system above carries this content). */}
      {!useNewArchitecture && (() => {

        const r = recommendation;
        if (!r) return null;
        const isPrompt = r.recommendation_type === "prompt_user";
        const promptText = isGuest
          ? "Tell us your size to get a recommendation"
          : "Add your size in settings →";
        const hasActionableDiag =
          !!r.diagnostic_message && r.missing_fields.length > 0;
        return (
          <div
            aria-label="Size recommendation"
            style={{
              marginTop: 0,
              marginBottom: 12,
              opacity: isComputing ? 0.6 : 1,
              transition: "opacity 0.2s ease",
            }}
          >
            <p
              className="l2f-type-secondary"
              style={{
                fontSize: 12,
                fontWeight: 400,
                lineHeight: 1.4,
                color: "#4A4540",
                margin: 0,
              }}
            >
              {isPrompt ? promptText : r.recommendation_text}
            </p>
            {showScrapePendingSubtext && (
              <p
                className="l2f-type-caption"
                style={{
                  color: "#6B6560",
                  fontStyle: "italic",
                  margin: "4px 0 0",
                }}
              >
                We're checking this item's fit data…
              </p>
            )}
            {(() => {
              // PR 6c.3: hoist LLM append above the list gate so a valid
              // stylingCopy can render even when base reasoning_rows is empty
              // (e.g. Mango outfits whose brand verdict lives in r.reasoning
              // prose, not in reasoning_rows). When both are empty the <ul>
              // still doesn't render — graceful degradation preserved.
              const base = r.reasoning_rows ?? [];
              const hasSparkle = base.some((row) => row.icon === "sparkle");
              const effective =
                !hasSparkle && llmStylingCopy
                  ? [
                      ...base,
                      {
                        icon: "sparkle" as const,
                        type: "styling" as const,
                        text: llmStylingCopy,
                      },
                    ]
                  : base;
              if (effective.length === 0) return null;
              return (
                <ul
                  aria-label="Fit reasoning"
                  style={{
                    listStyle: "none",
                    padding: 0,
                    margin: "8px 0 0",
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                  }}
                >
                  {effective.slice(0, 5).map((row, idx) => {
                    if (row.icon === "sparkle") {
                      return (
                        <li
                          key={`sparkle-${idx}`}
                          className="l2f-type-secondary"
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 6,
                            color: "#26215C",
                            lineHeight: 1.4,
                            fontWeight: 500,
                            background: "#EEEDFE",
                            padding: 8,
                            borderRadius: 6,
                          }}
                        >
                          <span
                            aria-hidden
                            style={{ color: "#534AB7", fontSize: 12, flexShrink: 0, lineHeight: 1.4 }}
                          >
                            ✦
                          </span>
                          <span>{row.text}</span>
                        </li>
                      );
                    }
                    const colour =
                      row.icon === "warning"
                        ? "#C84B2F"
                        : row.icon === "check"
                          ? "var(--l2f-success-ink-soft, #5A8F4F)"
                          : "#6B6560";
                    const glyph =
                      row.icon === "warning" ? "⚠" : row.icon === "check" ? "✓" : "·";
                    return (
                      <li
                        key={`${row.icon}-${idx}`}
                        className="l2f-type-secondary"
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 6,
                          color: "#2C2820",
                          lineHeight: 1.4,
                        }}
                      >
                        <span
                          aria-hidden
                          style={{ color: colour, fontSize: 12, flexShrink: 0, lineHeight: 1.4 }}
                        >
                          {glyph}
                        </span>
                        <span>{row.text}</span>
                      </li>
                    );
                  })}
                </ul>
              );
            })()}
            {r.diagnostic_message && (
              <button
                type="button"
                onClick={hasActionableDiag ? handleDiagnosticClick : undefined}
                disabled={!hasActionableDiag}
                className="l2f-type-caption"
                style={{
                  marginTop: 6,
                  padding: "12px 8px",
                  minHeight: 44,
                  background: "transparent",
                  border: "none",
                  textAlign: "left",
                  display: "block",
                  width: "100%",
                  cursor: hasActionableDiag ? "pointer" : "default",
                  color: hasActionableDiag ? CORAL : "#9B958E",
                  textDecoration: hasActionableDiag ? "underline" : "none",
                  lineHeight: 1.4,
                }}
              >
                <span>{r.diagnostic_message}</span>
                {hasActionableDiag && <span> →</span>}
              </button>
            )}
          </div>
        );
      })()}


      {/* BRAND FIT RE-PROMPT (auth-only, default brands) */}
      {shouldShowReprompt && canonicalRetailer && repromptCategory && (
        <div
          aria-label="Brand fit re-prompt"
          style={{
            marginTop: 12,
            background: "#FFFCF8",
            border: "0.5px solid #E8E2D9",
            borderRadius: 12,
            padding: "10px 12px",
            position: "relative",
          }}
        >
          <button
            type="button"
            aria-label="Skip brand fit prompt"
            onClick={handleDismissReprompt}
            disabled={dismissing}
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              background: "transparent",
              border: "none",
              cursor: dismissing ? "default" : "pointer",
              fontSize: 16,
              lineHeight: 1,
              color: "#9B958E",
              padding: 14,
              minHeight: 44,
              minWidth: 44,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ✕
          </button>
          <p
            className="l2f-type-secondary"
            style={{
              color: "#4A4540",
              margin: 0,
              paddingRight: 44,
              lineHeight: 1.4,
            }}
          >
            How does <strong style={{ fontWeight: 500 }}>{canonicalRetailer}</strong> fit you for {repromptCategory}?
          </p>
          <div
            role="group"
            aria-label="Brand fit verdict"
            style={{
              marginTop: 8,
              display: "flex",
              flexWrap: "wrap",
              gap: 6,
            }}
          >
            {(
              [
                { v: "true_to_size" as const, label: "Fits true" },
                { v: "runs_small" as const, label: "Runs small" },
                { v: "runs_large" as const, label: "Runs large" },
              ]
            ).map(({ v, label }) => {
              const isSelected = savingVerdict === v;
              return (
                <button
                  key={v}
                  type="button"
                  onClick={() => handleVerdictTap(v)}
                  disabled={savingVerdict !== null}
                  style={{
                    padding: isSelected ? "1.5px 12.5px" : "3px 14px",
                    minHeight: 44,
                    borderRadius: 14,
                    border: isSelected ? `1.5px solid ${CORAL}` : "0.5px solid #E8E2D9",
                    background: isSelected ? "#FAECE7" : "#F1EFE8",
                    fontFamily: "var(--font-body)",
                    fontSize: 13,
                    fontWeight: 500,
                    color: isSelected ? "#712B13" : "#4A4540",
                    cursor: savingVerdict !== null ? "default" : "pointer",
                    opacity: savingVerdict !== null && !isSelected ? 0.55 : 1,
                  }}
                >
                  {isSelected ? "Saving…" : label}
                </button>
              );
            })}
          </div>
        </div>
      )}


      {/* BEST-VISUAL-MATCH-1 — "Your pick" inline row.
          Anchored on Lens-#1 (post-attribute-guards) via backend tiers.your_pick.
          Renders on ALL verdicts (including Skip — accordion alternatives stay
          suppressed below per PQ-5e, but the source item itself remains tappable). */}
      <YourPickRow
        outfitId={outfitId}
        verdict={verdict}
        yourPick={(heroItem.tiers?.your_pick as ShoppingProduct | null | undefined) ?? null}
        isCaveat={!!heroItem.tiers?.your_pick_is_caveat}
      />

      {/* TIER CHIPS — PQ-5e: hidden behind "See alternatives" accordion.
          Suppressed entirely on Skip verdict (Skip-it doesn't route to
          alternatives). activeTier state remains at parent level so the
          price line + SizePills + guest garment data keep reacting even
          while chips are collapsed. */}

      {verdict !== "skip" && showChips && accordionAltTiers.length > 0 && (
        <>
          <button
            type="button"
            onClick={handleToggleExpanded}
            aria-expanded={expanded}
            aria-controls="tier-alternatives-content"
            style={{
              width: "100%",
              background: "transparent",
              border: "none",
              borderTop: "0.5px solid #E8E0D5",
              padding: "14px 16px",
              marginTop: 12,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              cursor: "pointer",
              minHeight: 44,
            }}
          >
            <span
              style={{
                fontFamily: "var(--font-display, var(--font-serif, serif))",
                fontStyle: "italic",
                fontSize: 13,
                color: "#6B6560",
              }}
            >
              See alternatives
            </span>
            <ChevronDown
              size={14}
              color="#6B6560"
              style={{
                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 200ms ease",
              }}
            />
          </button>

          {expanded && (() => {
            // BEST-VISUAL-MATCH-2 — accordion shows only Cheaper + Premium,
            // dedup'd against tiers.your_pick.link (filter applied above
            // when accordionAltTiers was built).
            const TIER_DISPLAY_LABEL: Record<TierName, string> = {
              budget: "Cheaper",
              mid: "Your pick",
              premium: "Premium",
            };
            const altTiers = accordionAltTiers;
            if (altTiers.length === 0) return null;
            return (
            <div
              ref={tierListRef}
              id="tier-alternatives-content"
              role="tablist"
              aria-label="Choose price tier"
              style={{
                marginTop: 12,
                marginBottom: 12,
                display: "grid",
                gridTemplateColumns: `repeat(${altTiers.length}, minmax(0, 1fr))`,
                gap: 6,
              }}
            >
              {altTiers.map((t) => {
                const product = heroItem.tiers![t] as ShoppingProduct;
                const tc = tierConfidence[t];
                const isActive = t === activeTier;
                let dotColor: string | null = null;
                let dotLabel: string | null = null;
                if (tc) {
                  if (tc.confidence_level === "high" || tc.confidence_level === "medium-high") {
                    dotColor = "var(--l2f-success-ink-soft)";
                    dotLabel = "Good fit";
                  } else if (tc.confidence_level === "medium") {
                    dotColor = "var(--l2f-warning-ink-soft)";
                    dotLabel = "Check fit";
                  } else {
                    dotColor = "var(--color-text-tertiary, #888)";
                    dotLabel = "Limited data";
                  }
                }
                return (
                  <button
                    key={t}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setActiveTier(t)}
                    style={{
                      background: isActive ? "#FAECE7" : "#FFFCF8",
                      border: isActive
                        ? `1.5px solid ${CORAL}`
                        : "0.5px solid #E8E2D9",
                      borderRadius: 10,
                      padding: isActive ? "7px 6.5px" : "8px 7.5px",
                      cursor: "pointer",
                      textAlign: "center",
                      fontFamily: "var(--font-body)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 2,
                      minWidth: 0,
                    }}
                  >
                    <span
                      className="l2f-type-section-header"
                      style={{
                        color: isActive ? CORAL : "#9B958E",
                      }}
                    >
                      {TIER_DISPLAY_LABEL[t]}
                    </span>
                    <span
                      style={{
                        fontSize: 15,
                        fontWeight: 600,
                        color: "#2C2820",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {product.price || "—"}
                    </span>
                    {dotColor && dotLabel && (
                      <span style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 2 }}>
                        <span
                          aria-hidden
                          style={{
                            display: "inline-block",
                            width: 6,
                            height: 6,
                            borderRadius: 999,
                            background: dotColor,
                          }}
                        />
                        <span style={{ fontSize: 12, color: "#6B6560" }}>
                          {dotLabel}
                        </span>
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            );
          })()}
        </>
      )}



      {/* Live-enriching indicator */}
      {enriching && (
        <>
          <style>{`@keyframes l2f-pulse-dot{0%,100%{opacity:.35;transform:scale(.85)}50%{opacity:1;transform:scale(1)}}`}</style>
          <span
            aria-hidden
            style={{
              position: "absolute",
              top: 10,
              right: 12,
              width: 8,
              height: 8,
              borderRadius: 999,
              background: CORAL,
              animation: "l2f-pulse-dot 1.2s ease-in-out infinite",
            }}
          />
        </>
      )}

      {/* F30.3 — SIZE PILLS (auth-only).
          Read-only display of available sizes for the active tier with the
          recommended size highlighted. Replaces the retired FIT CONFIDENCE
          panel (section H) — the "Recommended size: M" + missing-size CTA
          live here now. Guests collect size through GuestProgressiveFit. */}
      {!isGuest && (() => {
        const e = tierConfidence[activeTier] ?? null;
        const recommendedSize = e?.recommended_size ?? null;
        const sizeChart = activeGarment?.size_chart ?? null;
        const sizeChartKeys = sizeChart ? Object.keys(sizeChart) : [];
        const sizes = sizeChartKeys.length > 0
          ? sizeChartKeys
          : ["XS", "S", "M", "L", "XL"];
        if (!recommendedSize && !sizeChart) return null;
        return (
          <SizePills
            sizes={sizes}
            recommendedSize={recommendedSize}
            missingSize={!authSize}
            onAddSize={() => requestAccountFocus("size")}
          />
        );
      })()}


      {/* F30.1 — VERSATILITY and WHAT YOU NEED sections removed.
          Versatility will return as a sibling card AFTER LB3 in F30.5.
          Gap items deleted permanently (duplicated by LB1 shopping list). */}



      {/* GUEST FIT COLLECTOR + PRODUCT DETAILS (guest-only).
          Moved here so guests see the decision-support sections first and the
          form sits immediately above the primary CTA. */}
      {isGuest && fit && (() => {
        const g = guestGarmentData[activeTier];
        const retailer =
          (heroItem.tiers?.[activeTier] as ShoppingProduct | null)?.source ?? null;
        const sizeInChart = (() => {
          if (!guestProfile.size || !g?.size_chart) return false;
          const keys = Object.keys(g.size_chart).map((k) => k.toUpperCase());
          return keys.includes(guestProfile.size.toUpperCase());
        })();
        const mr = g?.model_reference ?? null;
        const modelLine =
          mr && mr.model_height && mr.size_worn
            ? `${mr.model_height}, wears size ${mr.size_worn}`
            : null;
        const showChart = sizeChartHasMeasurements(g?.size_chart);
        const hasGarmentData =
          !!g &&
          (g.fabric_composition || g.fit_type || modelLine || g.fit_copy || showChart);

        // Build B follow-up: per-tier retailer + chart info for adaptive
        // numeric-size form (drives detectTierConventions in the child).
        const heroTiers = (TIER_ORDER as TierName[]).map((t) => {
          const p = heroItem?.tiers?.[t] as ShoppingProduct | null | undefined;
          const link = p?.link ?? null;
          const row = guestGarmentData[t];
          return {
            retailer_id: retailerIdFromLink(link),
            size_chart: (row?.size_chart as Record<string, unknown> | null) ?? null,
          };
        });

        return (
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: "0.5px solid #F1EFE8" }}>
            <GuestProgressiveFit
              gender={guestGender}
              outfitId={outfitId}
              fitType={g?.fit_type ?? null}
              fabricComposition={g?.fabric_composition ?? null}
              category={heroItem.category ?? null}
              sizeInChart={sizeInChart}
              retailerName={retailer}
              heroTiers={heroTiers}
              assessment={assessment}
              focusFieldRequest={focusFieldRequest}
              onProfileChange={setGuestProfile}
              onRegenerate={onGuestRegenerate}
              regenStatus={guestRegenStatus}
              regenError={guestRegenError}
            />


            {hasGarmentData && (
              <div
                style={{
                  marginTop: 12,
                  background: MUTED_BG,
                  border: `1px solid ${MUTED_BORDER}`,
                  borderRadius: 12,
                  padding: 12,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span className="l2f-type-section-header" style={{ color: "var(--foreground)" }}>
                    Product details
                  </span>
                </div>
                <ul style={{ margin: "8px 0 0", padding: 0, listStyle: "none" }}>
                  {g!.fabric_composition && (
                    <li className="l2f-type-caption" style={{ color: "var(--foreground)", marginTop: 4 }}>
                      <strong style={{ fontWeight: 600 }}>Fabric:</strong> {g!.fabric_composition}
                    </li>
                  )}
                  {g!.fit_type && (
                    <li className="l2f-type-caption" style={{ color: "var(--foreground)", marginTop: 4 }}>
                      <strong style={{ fontWeight: 600 }}>Fit:</strong> {g!.fit_type}
                    </li>
                  )}
                  {modelLine && (
                    <li className="l2f-type-caption" style={{ color: "var(--foreground)", marginTop: 4 }}>
                      <strong style={{ fontWeight: 600 }}>Model:</strong> {modelLine}
                    </li>
                  )}
                  {g!.fit_copy && (
                    <li
                      className="l2f-type-caption"
                      style={{
                        color: "var(--foreground)",
                        marginTop: 6,
                        fontStyle: "italic",
                      }}
                    >
                      “{g!.fit_copy}”
                    </li>
                  )}
                </ul>
                {showChart && (
                  <div style={{ marginTop: 10 }}>
                    <button
                      type="button"
                      onClick={() => setSizeChartOpen((v) => !v)}
                      style={{
                        background: "transparent",
                        border: "none",
                        padding: "12px 8px",
                        cursor: "pointer",
                        fontSize: 13,
                        fontWeight: 600,
                        color: CORAL,
                        fontFamily: "var(--font-body)",
                        minHeight: 44,
                      }}
                      aria-expanded={sizeChartOpen}
                    >
                      {sizeChartOpen ? "Hide size chart" : "View size chart"}
                    </button>
                    {sizeChartOpen && g!.size_chart && (
                      <div style={{ marginTop: 8, overflowX: "auto" }}>
                        <table
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            fontSize: 12,
                            color: "var(--foreground)",
                          }}
                        >
                          <thead>
                            <tr>
                              <th
                                style={{
                                  textAlign: "left",
                                  padding: "4px 6px",
                                  borderBottom: "0.5px solid var(--border)",
                                  fontWeight: 600,
                                }}
                              >
                                Size
                              </th>
                              {(() => {
                                const cols = new Set<string>();
                                for (const row of Object.values(g!.size_chart!)) {
                                  if (row && typeof row === "object") {
                                    for (const k of Object.keys(row)) cols.add(k);
                                  }
                                }
                                return Array.from(cols).map((c) => (
                                  <th
                                    key={c}
                                    style={{
                                      textAlign: "left",
                                      padding: "4px 6px",
                                      borderBottom: "0.5px solid var(--border)",
                                      fontWeight: 600,
                                      textTransform: "capitalize",
                                    }}
                                  >
                                    {c.replace(/_/g, " ")}
                                  </th>
                                ));
                              })()}
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(g!.size_chart!).map(([sizeKey, row]) => {
                              const cols = new Set<string>();
                              for (const r of Object.values(g!.size_chart!)) {
                                if (r && typeof r === "object") {
                                  for (const k of Object.keys(r)) cols.add(k);
                                }
                              }
                              return (
                                <tr key={sizeKey}>
                                  <td
                                    style={{
                                      padding: "4px 6px",
                                      borderBottom: "0.5px solid var(--border)",
                                      fontWeight: 600,
                                    }}
                                  >
                                    {sizeKey}
                                  </td>
                                  {Array.from(cols).map((c) => {
                                    const v = row?.[c];
                                    const display =
                                      v == null || String(v).toLowerCase() === "null"
                                        ? "—"
                                        : String(v);
                                    return (
                                      <td
                                        key={c}
                                        style={{
                                          padding: "4px 6px",
                                          borderBottom: "0.5px solid var(--border)",
                                        }}
                                      >
                                        {display}
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* PRIMARY CTA — guest-only action at the bottom of the card.
          F30.1: removed auth-only "Generating your look…" helper — LB1 has
          its own LookbookLoadingSilhouette. Auth users render no CTA here. */}
      {(() => {
        if (!isGuest) return null;



        const isLoadingRegen = guestRegenStatus === "loading";
        const isErrorRegen = guestRegenStatus === "error";
        const guestStepComplete =
          !!guestProfile.size &&
          !!guestProfile.model.height &&
          !!guestProfile.model.body_shape &&
          (guestGender === "men" || !!guestProfile.model.body_size);
        const disabled = !guestStepComplete;

        if (isLoadingRegen) {
          return (
            <div style={{ marginTop: 18 }}>
              <LookbookLoadingSilhouette
                modelProfile={{
                  model_gender: guestGender,
                  model_height: guestProfile.model.height,
                  model_body_shape: guestProfile.model.body_shape,
                  model_body_size: guestProfile.model.body_size,
                  model_skin_tone: null,
                  model_hair: null,
                }}
                label="Personalising"
                messages={[
                  "Building your model…",
                  "Styling the outfit on you…",
                  "Composing the editorial…",
                  "Almost there…",
                ]}
              />
            </div>
          );
        }

        const handleClick = () => {
          if (disabled) return;
          trackEvent("confidence_card_regen_triggered", {
            outfit_id: outfitId,
            user_type: "guest",
          });
          onGuestRegenerate?.(guestProfile.model, guestProfile.size);
        };

        return (
          <div style={{ marginTop: 14 }}>
            <button
              type="button"
              onClick={handleClick}
              disabled={disabled}
              style={{
                width: "100%",
                background: CORAL,
                color: "#fff",
                border: "none",
                padding: "10px 16px",
                borderRadius: 22,
                fontSize: 15,
                fontWeight: 600,
                opacity: disabled ? 0.5 : 1,
                cursor: disabled ? "default" : "pointer",
                fontFamily: "var(--font-body)",
                transition: "opacity 0.15s ease",
                minHeight: 44,
              }}
            >
              {isErrorRegen ? "Couldn't update — try again" : "See yourself in this look →"}
            </button>
            {isErrorRegen && guestRegenError && (
              <p
                className="l2f-type-caption"
                style={{
                  color: "var(--muted-foreground)",
                  textAlign: "center",
                  marginTop: 8,
                }}
              >
                {guestRegenError}
              </p>
            )}
          </div>
        );
      })()}


    </section>
  );
}
